import type {
  NovaClarificationAnswer,
  NovaClarificationOption,
  NovaClarificationToolUIPart,
} from '@newfront-insurance/ai-api';
import type { UIDataTypes, UIMessagePart, UITools } from 'ai';

export type ClarificationInputType = 'text' | 'select' | 'multi-select';

export type ClarificationOption = NovaClarificationOption;
export interface ClarificationQuestion {
  questionId: string;
  header: string;
  question: string;
  helperText?: string;
  inputType: ClarificationInputType;
  options?: ClarificationOption[];
}

export type ClarificationAnswer = NovaClarificationAnswer;
export interface ClarificationInput {
  questions: ClarificationQuestion[];
}

interface ClarificationOptionLike {
  optionId?: string;
  label?: string;
  description?: string;
}

interface ClarificationQuestionLike {
  questionId?: string;
  header?: string;
  question?: string;
  helperText?: string;
  inputType?: unknown;
  options?: ClarificationOptionLike[];
}

export interface ClarificationMetadata {
  state: 'awaiting-clarification' | 'clarification-resolved';
  activeToolCallId: string;
  rawInput?: unknown;
  resolvedTranscript?: string;
}

export type ClarificationToolPart = NovaClarificationToolUIPart;

export interface ClarificationAwareMessage {
  role: 'system' | 'user' | 'assistant';
  metadata?: {
    clarification?: ClarificationMetadata;
  };
  parts: UIMessagePart<UIDataTypes, UITools>[];
}

export function isClarificationToolPart(part: UIMessagePart<UIDataTypes, UITools>): part is ClarificationToolPart {
  return part.type === 'tool-clarification';
}

export function getClarificationPart(message?: ClarificationAwareMessage | null): ClarificationToolPart | undefined {
  return message?.parts.slice().reverse().find(isClarificationToolPart);
}

export function isClarificationPending(message?: ClarificationAwareMessage | null): boolean {
  const clarificationPart = getClarificationPart(message);

  if (clarificationPart?.state === 'input-available') {
    return true;
  }

  if (clarificationPart?.state === 'output-available') {
    return false;
  }

  return message?.metadata?.clarification?.state === 'awaiting-clarification';
}

export function getLatestPendingClarification<M extends ClarificationAwareMessage>(
  messages: readonly M[],
): { message: M; part: ClarificationToolPart } | undefined {
  const lastMessage = messages.at(-1);

  if (lastMessage?.role !== 'assistant') {
    return undefined;
  }

  const part = getClarificationPart(lastMessage);
  if (part?.state !== 'input-available') {
    return undefined;
  }

  return { message: lastMessage, part };
}

export function getResolvedClarificationTranscript(
  message?: ClarificationAwareMessage | null,
  part?: ClarificationToolPart | null,
): string | undefined {
  const latestClarificationPart = getClarificationPart(message);
  const metadataTranscript =
    message?.metadata?.clarification?.state === 'clarification-resolved'
      ? message.metadata.clarification.resolvedTranscript
      : undefined;

  if (metadataTranscript && (!part || part.toolCallId === latestClarificationPart?.toolCallId)) {
    return metadataTranscript;
  }

  const clarificationPart = part ?? latestClarificationPart;
  if (clarificationPart?.state !== 'output-available') {
    return undefined;
  }

  const outputTranscript = getClarificationTranscriptFromOutput(clarificationPart.output);
  if (outputTranscript) {
    return outputTranscript;
  }

  const questions = normalizeClarificationInput(clarificationPart.input).questions;
  const answers = normalizeClarificationOutputAnswers(clarificationPart.output);

  if (questions.length === 0 || answers.length === 0) {
    return undefined;
  }

  return buildClarificationTranscript(questions, answers);
}

export function normalizeClarificationInput(input: unknown): ClarificationInput {
  if (!isObject(input) || !Array.isArray(input.questions)) {
    return { questions: [] };
  }

  return {
    questions: input.questions
      .map((question, questionIndex) => normalizeClarificationQuestion(question, questionIndex))
      .filter((question): question is ClarificationQuestion => !!question),
  };
}

export function buildClarificationTranscript(
  questions: ClarificationQuestion[],
  answers: ClarificationAnswer[],
): string {
  return questions
    .map((question) => {
      const answer = answers.find((candidate) => candidate.questionId === question.questionId);
      const resolvedAnswer = answer ? getClarificationAnswerText(question, answer) : 'Skipped';

      return `Q: ${question.question}\nA: ${resolvedAnswer}`;
    })
    .join('\n\n');
}

export function getClarificationAnswerText(question: ClarificationQuestion, answer: ClarificationAnswer): string {
  if (isSkippedClarificationAnswer(answer)) {
    return 'Skipped';
  }

  if (isMultiSelectClarificationAnswer(answer)) {
    const selectedOptions = answer.optionIds
      .map((optionId) => getClarificationOptionLabel(question, optionId))
      .join(', ');
    const additionalText = getClarificationAdditionalText(answer);

    if (selectedOptions && additionalText) {
      return `${selectedOptions}. Additional details: ${additionalText}`;
    }

    return selectedOptions || additionalText || 'Skipped';
  }

  if (isSingleSelectClarificationAnswer(answer)) {
    return getClarificationOptionLabel(question, answer.optionId);
  }

  if (isTextClarificationAnswer(answer)) {
    return answer.text.trim();
  }

  return 'Skipped';
}

function normalizeClarificationQuestion(question: unknown, questionIndex: number): ClarificationQuestion | null {
  if (!isObject(question)) {
    return null;
  }

  const typedQuestion = question as ClarificationQuestionLike;
  const questionText = typedQuestion.question?.trim();
  const questionId = typedQuestion.questionId?.trim() || `q${questionIndex + 1}`;
  const header = typedQuestion.header?.trim() || `Question ${questionIndex + 1}`;

  if (!questionText) {
    return null;
  }

  const normalizedOptions = typedQuestion.options
    ?.map((option, optionIndex) => normalizeClarificationOption(option, questionIndex, optionIndex))
    .filter((option): option is ClarificationOption => !!option);

  const inputType = normalizeClarificationInputType(typedQuestion);
  if (!inputType) {
    return null;
  }

  const supportsOptions = inputType !== 'text';
  if (supportsOptions && !normalizedOptions?.length) {
    return null;
  }

  return {
    questionId,
    header,
    question: questionText,
    helperText: typedQuestion.helperText?.trim() || undefined,
    inputType,
    options: supportsOptions ? normalizedOptions : undefined,
  };
}

function normalizeClarificationInputType(question: ClarificationQuestionLike): ClarificationInputType | null {
  if (question.inputType === 'text' || question.inputType === 'select' || question.inputType === 'multi-select') {
    return question.inputType;
  }

  return null;
}

function normalizeClarificationOption(
  option: ClarificationOptionLike | undefined,
  questionIndex: number,
  optionIndex: number,
): ClarificationOption | null {
  const optionId = option?.optionId?.trim() || `q${questionIndex + 1}_o${optionIndex + 1}`;
  const label = option?.label?.trim();

  if (!option || !label) {
    return null;
  }

  return {
    optionId,
    label,
    description: option.description?.trim() || undefined,
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getClarificationTranscriptFromOutput(output: unknown): string | undefined {
  if (!isObject(output)) {
    return undefined;
  }

  const transcript = output.transcript;
  return typeof transcript === 'string' && transcript.trim().length > 0 ? transcript.trim() : undefined;
}

function normalizeClarificationOutputAnswers(output: unknown): ClarificationAnswer[] {
  if (!isObject(output) || !Array.isArray(output.answers)) {
    return [];
  }

  return output.answers
    .map((answer) => normalizeClarificationAnswer(answer))
    .filter((answer): answer is ClarificationAnswer => !!answer);
}

function normalizeClarificationAnswer(answer: unknown): ClarificationAnswer | null {
  if (!isObject(answer) || typeof answer.questionId !== 'string') {
    return null;
  }

  const questionId = answer.questionId.trim();

  if (!questionId) {
    return null;
  }

  if (answer.skipped === true) {
    return {
      questionId,
      skipped: true,
    };
  }
  const text = typeof answer.text === 'string' && answer.text.trim() ? answer.text.trim() : undefined;

  if (Array.isArray(answer.optionIds)) {
    const optionIds = answer.optionIds
      .filter((optionId): optionId is string => typeof optionId === 'string')
      .map((optionId) => optionId.trim())
      .filter(Boolean);

    if (optionIds.length > 0) {
      return {
        questionId,
        optionIds,
        text,
      };
    }
  }

  if (typeof answer.optionId === 'string' && answer.optionId.trim()) {
    return {
      questionId,
      optionId: answer.optionId.trim(),
    };
  }

  if (text) {
    return {
      questionId,
      text,
    };
  }

  return null;
}

function getClarificationOptionLabel(question: ClarificationQuestion, optionId: string): string {
  return question.options?.find((option: ClarificationOption) => option.optionId === optionId)?.label ?? optionId;
}

function getClarificationAdditionalText(
  answer: Extract<ClarificationAnswer, { optionIds: string[] }>,
): string | undefined {
  return typeof answer.text === 'string' && answer.text.trim() ? answer.text.trim() : undefined;
}

function isSkippedClarificationAnswer(
  answer: ClarificationAnswer,
): answer is Extract<ClarificationAnswer, { skipped: true }> {
  return 'skipped' in answer && answer.skipped === true;
}

function isTextClarificationAnswer(
  answer: ClarificationAnswer,
): answer is Extract<ClarificationAnswer, { text: string }> {
  return 'text' in answer && typeof answer.text === 'string';
}

function isMultiSelectClarificationAnswer(
  answer: ClarificationAnswer,
): answer is Extract<ClarificationAnswer, { optionIds: string[] }> {
  return 'optionIds' in answer && Array.isArray(answer.optionIds);
}

function isSingleSelectClarificationAnswer(
  answer: ClarificationAnswer,
): answer is Extract<ClarificationAnswer, { optionId: string }> {
  return 'optionId' in answer && typeof answer.optionId === 'string';
}
